import initialState from '@redux/experiments/initialState';
import { experiments } from '@settings';
import type { Id } from '@typings/experiments';
import { constructAnalyticsProperties } from '@utils/constructAnalyticsProperties';
import { noop } from '@utils/noop';
import analyticsWrapper from '@xo-union/tk-analytics';
import React, { useCallback, useMemo } from 'react';
import type { Track } from 'types/analytics';
import type { FCWithChildren } from 'types/react-extended';

const VENDOR_CATEGORY_VIEWED_EVENT = 'Vendor Category Viewed';
const VENDOR_CATEGORY_VIEWED_DEDUPE_WINDOW_MS = 10_000;

const vendorCategoryViewedDedupeTimestamps = new Map<string, number>();

/** @internal Test-only reset for module-level dedupe state. */
export const __resetVendorCategoryViewedDedupeForTests = () => {
	vendorCategoryViewedDedupeTimestamps.clear();
};

const shouldDedupeVendorCategoryViewed = (dedupeKey: string, now: number) => {
	for (const [key, ts] of vendorCategoryViewedDedupeTimestamps.entries()) {
		if (now - ts >= VENDOR_CATEGORY_VIEWED_DEDUPE_WINDOW_MS) {
			vendorCategoryViewedDedupeTimestamps.delete(key);
		}
	}

	const lastSeen = vendorCategoryViewedDedupeTimestamps.get(dedupeKey);
	if (
		lastSeen != null &&
		now - lastSeen < VENDOR_CATEGORY_VIEWED_DEDUPE_WINDOW_MS
	) {
		return true;
	}

	vendorCategoryViewedDedupeTimestamps.set(dedupeKey, now);
	return false;
};

const asStringOrNull = (value: unknown): string | null => {
	return typeof value === 'string' && value.length > 0 ? value : null;
};

const asNumberOrNull = (value: unknown): number | null => {
	return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const buildVendorCategoryViewedDedupeKey = (
	pathname: string,
	search: string,
	properties: Record<string, unknown>,
): string => {
	const canonicalIdentity = {
		displayLocationName: asStringOrNull(properties.displayLocationName),
		displayMarketCode: asStringOrNull(properties.displayMarketCode),
		pageNumber: asNumberOrNull(properties.pageNumber),
		searchTerm: asStringOrNull(properties.searchTerm),
		sourcePage: asStringOrNull(properties.sourcePage),
		vendorCategoryCode: asStringOrNull(properties.vendorCategoryCode),
		vendorCategoryLevel: asStringOrNull(properties.vendorCategoryLevel),
	};

	const hasCanonicalIdentity = Object.values(canonicalIdentity).some(
		(value) => value !== null,
	);

	if (hasCanonicalIdentity) {
		// Property-scoped dedupe: classifier sync can briefly emit twice with the
		// same logical page-view identity while URL is transitioning. Ignore
		// payload jitter (e.g. was_auto_selected toggling). Include query string
		// so real filter changes (which update search params) still emit a new
		// event.
		return `${VENDOR_CATEGORY_VIEWED_EVENT}|${JSON.stringify(canonicalIdentity)}|${search}`;
	}

	// Fallback for unexpected payload shapes.
	return `${VENDOR_CATEGORY_VIEWED_EVENT}|${pathname}${search}`;
};

interface AnalyticsContextValues {
	track: Track;
}

interface Props {
	reduxExperiments?: Redux.Experiments;
}
export const AnalyticsContext = React.createContext<AnalyticsContextValues>({
	track: noop,
});

AnalyticsContext.displayName = 'AnalyticsContext';

const AnalyticsProvider: FCWithChildren<Props> = ({
	children,
	reduxExperiments = initialState,
}) => {
	const tkwwFlipperIds = useMemo(() => {
		const { assignments, configurations } = reduxExperiments;
		const assignedVariants = Object.entries(assignments).reduce(
			(acc, [key, value]) => {
				const variantId = (configurations[key as Id]?.variants || []).find(
					(variant) => variant.variant_name === value,
				)?.variant_id;
				if (variantId) acc[key] = variantId;
				return acc;
			},
			{} as { [key: string]: string },
		);
		return { 'TKWW-Flipper-IDs': assignedVariants };
	}, [reduxExperiments]);

	// `state.experiments.gatekeeper` (dev/QA `tkwwemo`-style overrides deserialized in
	// client/redux/experiments/utils.ts) is intentionally not reflected here. It only ever
	// affects what selectAssignment()-based consumers (e.g. Flipper's reportServerSideExperiment)
	// see; this stamp reads reduxExperiments.assignments directly, so gatekeeper never
	// influences TKWW-Flipper-IDs and has nothing to flag as overridden.
	const tkwwFlipperOverrides = useMemo(() => {
		const overrides = reduxExperiments.overrides || {};
		if (Object.keys(overrides).length === 0) return {};
		return { 'TKWW-Flipper-Overrides': { ...overrides } };
	}, [reduxExperiments]);

	// Gatekeeper (QA override) takes precedence over the assignment, mirroring selectAssignment().
	// gatekeeper is keyed by experiment name, unlike assignments, which is keyed by flipper ID.
	const gatekeeperImproveMpPerformanceTier2 =
		reduxExperiments.gatekeeper?.improveMpPerformanceTier2;
	const isImproveMpPerformanceTier2Enabled =
		(gatekeeperImproveMpPerformanceTier2 ||
			reduxExperiments.assignments?.[experiments.improveMpPerformanceTier2]) ===
		'variant1';

	const trackHandler: Track = (analyticsEvent, context) => {
		analyticsEvent.properties = {
			...analyticsEvent.properties,
			...tkwwFlipperIds,
			...tkwwFlipperOverrides,
		};

		if (analyticsEvent.event === VENDOR_CATEGORY_VIEWED_EVENT) {
			const pathname =
				typeof window !== 'undefined' ? window.location.pathname : '';
			const search =
				typeof window !== 'undefined' ? window.location.search : '';
			const dedupeKey = buildVendorCategoryViewedDedupeKey(
				pathname,
				search,
				analyticsEvent.properties,
			);
			const now = Date.now();

			if (shouldDedupeVendorCategoryViewed(dedupeKey, now)) {
				return;
			}
		}

		if (process.env.NODE_ENV !== 'production') {
			// biome-ignore lint/suspicious/noConsoleLog: Logging analytics on QA
			console.log(
				`****Analytics event: ${analyticsEvent.event}****`,
				analyticsEvent,
			);
		}
		analyticsWrapper.track(
			analyticsEvent.event,
			constructAnalyticsProperties(analyticsEvent.properties),
			context,
		);
	};
	// biome-ignore lint/correctness/useExhaustiveDependencies: linter can't see into the named trackHandler reference, but these deps are real (used in its closure)
	const trackMemo = useCallback(trackHandler, [
		tkwwFlipperIds,
		tkwwFlipperOverrides,
	]);
	const track: Track = isImproveMpPerformanceTier2Enabled
		? trackMemo
		: trackHandler;

	const valueMemo = useMemo(() => ({ track }), [track]);
	const value = isImproveMpPerformanceTier2Enabled ? valueMemo : { track };

	return (
		<AnalyticsContext.Provider value={value}>
			{children}
		</AnalyticsContext.Provider>
	);
};

export default AnalyticsProvider;
